import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import { useEffect, useRef } from "react";

/**
 * Wires the shared Embla behaviours (last-slide detection, controlled index sync,
 * scroll-to-selected).
 *
 * PERF: Embla's `.on()` APPENDS to an internal handler list. The previous version
 * subscribed in an effect whose deps included callbacks recreated on every render and
 * never called `.off()`, so each re-render added another copy of every handler — with
 * 17 carousel consumers this measured as 5,331 listeners and a 71MB heap after a single
 * scroll. Handlers are now stable (they read the latest props through refs) and the
 * subscription is torn down properly, so `emblaApi` is the only dependency.
 */
const useEmblaSliderFeatures = (
  emblaApi: ReturnType<typeof useEmblaCarousel>[1],
  selectedIndex?: number,
  setSelectedIndex?: (selectedIndex: number) => void,
  setisLastSlide?: (isLastSlide: boolean) => void,
) => {
  // Preserve the original semantics: a negative index clamps to 0, and
  // `selectedIndex` === undefined means "uncontrolled — do not scroll".
  const targetIndex =
    selectedIndex !== undefined && selectedIndex < 0 ? 0 : (selectedIndex as number | undefined);

  // Latest-value refs so the Embla handlers keep a stable identity. Synced in an effect
  // rather than during render — mutating a ref while rendering is not allowed.
  const setSelectedIndexRef = useRef(setSelectedIndex);
  const setisLastSlideRef = useRef(setisLastSlide);
  const targetIndexRef = useRef(targetIndex);
  useEffect(() => {
    setSelectedIndexRef.current = setSelectedIndex;
    setisLastSlideRef.current = setisLastSlide;
    targetIndexRef.current = targetIndex;
  });

  useEffect(() => {
    if (!emblaApi) return;

    const onSlidesInView = (api: UseEmblaCarouselType[1] | undefined) => {
      if (!api) return;
      const maxSlideIdx = api.slideNodes()?.length - 1;
      if (api.slidesInView().includes(maxSlideIdx)) {
        setisLastSlideRef.current?.(true);
      }
    };

    const onSlidesChanged = () => {
      if (targetIndexRef.current === undefined) return;
      emblaApi.scrollTo(targetIndexRef.current);
    };

    const onSelect = () => {
      setSelectedIndexRef.current?.(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("slidesInView", onSlidesInView);
    emblaApi.on("slidesChanged", onSlidesChanged);
    emblaApi.on("select", onSelect);

    return () => {
      // Without .off() each re-subscribe leaves the previous copy attached.
      emblaApi.off("slidesInView", onSlidesInView);
      emblaApi.off("slidesChanged", onSlidesChanged);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Scrolling to the controlled index is a side effect — it must not run during render.
  useEffect(() => {
    if (!emblaApi || targetIndex === undefined) return;
    emblaApi.scrollTo(targetIndex);
  }, [emblaApi, targetIndex]);
};
export default useEmblaSliderFeatures;
